import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material'
import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import Loading from 'src/v3/components/loading'
import { selectUseEvents, selectUserID } from 'src/v3/settings/settings.slice'
import {
  selectEventById,
  selectEventsStatus,
  fetchEvents,
  selectCompletionEventsByID,
  selectCompletionEventsStatusByID,
  fetchEventCompletionEvents,
  selectQuizByEvent,
  selectQuizStatusByEvent,
  fetchQuiz,
  selectQuizSessionResults,
  selectQuizSessionResultsStatus,
  fetchQuizSessionResult,
  selectEventCERecordsStatus,
  selectEventCERecords,
  fetchEventCERecords,
} from './events.slice'
import { CertificateIcon } from '../core/icons'
import Survey, { SurveyFormValues } from 'src/v3/components/survey'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { eventId } = useParams()
  let eventID = eventId || ''

  let useEvents = useAppSelector(selectUseEvents)
  let userID = useAppSelector(selectUserID)

  let event = useAppSelector(selectEventById(eventID))
  let eventsStatus = useAppSelector(selectEventsStatus)
  let quiz = useAppSelector(selectQuizByEvent(eventID))
  let quizStatus = useAppSelector(selectQuizStatusByEvent(eventID))
  let sessionResults = useAppSelector(selectQuizSessionResults(eventID))
  let sessionResultsStatus = useAppSelector(
    selectQuizSessionResultsStatus(eventID),
  )
  let completionEvents = useAppSelector(selectCompletionEventsByID(eventID))
  let completionEventsStatus = useAppSelector(
    selectCompletionEventsStatusByID(eventID),
  )
  let ceRecords = useAppSelector(selectEventCERecords(eventID))
  let ceRecordsStatus = useAppSelector(selectEventCERecordsStatus(eventID))

  let [ceRecordID, setCERecordID] = useState<string>()
  let [loading, setLoading] = useState<boolean>(false)
  let [downloading, setDownloading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let allFulfilled =
    eventsStatus === 'fulfilled' &&
    completionEventsStatus === 'fulfilled' &&
    sessionResultsStatus === 'fulfilled' &&
    ceRecordsStatus === 'fulfilled'

  let hasQuiz = !!quiz
  let hasSurvey = !event?.linkedServiceID
  let hasHours = !!event?.hours
  let eventCompleted = hasQuiz
    ? completionEvents?.length > 0 && sessionResults?.passed
    : completionEvents?.length > 0
  let showSurvey = !hasSurvey && ceRecords.length && allFulfilled

  let subtitleText = showSurvey
    ? 'To get credit for this event, please fill out and submit the survey below.'
    : 'You may click the button below to download your certificate for this event.'
  if (loading) {
    subtitleText =
      'Please wait while we submit your CE and generate your certificate. This may take a few moments.'
  }

  let ceRecordSubmit = () =>
    axios
      .post('/api/event/ce-record', {
        eventID,
        // passedQuizSessionID: completionEvents[0]?.sessionID,
      })
      .then(pluckData)
      .then((data) => setCERecordID(data?.id))
      .catch((err) => {
        setErrorMessage(
          err?.message || err?.data?.message || 'Error downloading certificate',
        )
      })

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if ((eventID && !quizStatus) || quizStatus === 'uninitialized') {
      dispatch(fetchQuiz(eventID))
    }
  }, [quizStatus, eventID])

  useEffect(() => {
    if (
      (eventID && !sessionResultsStatus) ||
      sessionResultsStatus === 'uninitialized'
    ) {
      dispatch(fetchQuizSessionResult(eventID))
    }
  }, [sessionResultsStatus, eventID])

  useEffect(() => {
    if (
      eventID &&
      (!completionEventsStatus || completionEventsStatus === 'uninitialized')
    ) {
      dispatch(fetchEventCompletionEvents({ eventID, userID }))
    }
  }, [completionEventsStatus, eventID])

  useEffect(() => {
    if (eventID && (!ceRecordsStatus || ceRecordsStatus === 'uninitialized')) {
      dispatch(fetchEventCERecords(eventID))
    } else if (ceRecordsStatus === 'fulfilled' && ceRecords.length) {
      setCERecordID(ceRecords[0].ceRecordID)
    }
  }, [ceRecordsStatus, eventID])

  useEffect(() => {
    if (allFulfilled && eventCompleted && hasHours && !hasSurvey) {
      setLoading(true)
      ceRecordSubmit().finally(() => setLoading(false))
    } else if (allFulfilled && !eventCompleted) {
      navigate(`/events/${eventID}`)
    }
  }, [
    eventsStatus,
    completionEventsStatus,
    sessionResultsStatus,
    ceRecordsStatus,
  ])

  return useEvents ? (
    <Container sx={{ maxWidth: 1600 }}>
      <Box sx={{ mt: -18 }}></Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          <CertificateIcon />
        </Avatar>
        <Typography variant="h4">Event Certificate</Typography>
      </Box>
      {event?.banner && (
        <img
          style={{
            width: '100%',
            aspectRatio: '4 / 1',
            borderRadius: '10px',
            marginTop: 12,
            marginBottom: 12,
          }}
          src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event?.banner?.replace('originals', 'resized')}`}
        />
      )}
      <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
        Thank you for attending {event?.name}! We hope you enjoyed it.{' '}
        {subtitleText}
      </Typography>
      {loading && (
        <Box sx={{ my: 1 }}>
          <LinearProgress />
        </Box>
      )}
      {showSurvey && (
        <Survey
          domain="Event"
          onSubmit={async (values: SurveyFormValues) => {
            setLoading(true)
            return axios
              .post('/api/event/survey', { eventID, ...values })
              .then(() => (hasHours ? ceRecordSubmit() : Promise.resolve()))
              .catch((err) => {
                setErrorMessage(
                  err?.message ||
                    err?.data?.message ||
                    'Error submitting survey',
                )
              })
              .finally(() => setLoading(false))
          }}
        />
      )}
      {ceRecordID && (
        <Button
          sx={{ mt: -1 }}
          variant="contained"
          color="secondary"
          loading={downloading}
          onClick={() => {
            axios
              .get(`/api/event/certificate?ceRecordID=${ceRecordID}`)
              .then((response) => {
                axios
                  .get(response.data, { responseType: 'blob' })
                  .then((response) => {
                    let url = window.URL.createObjectURL(
                      new Blob([response.data]),
                    )
                    let link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', `${event?.name}.pdf`)
                    document.body.appendChild(link)
                    link.click()
                  })
                  .catch((err) => {
                    setErrorMessage(
                      err?.message ||
                        err?.data?.message ||
                        'Error downloading certificate',
                    )
                  })
                  .finally(() => setDownloading(false))
              })
          }}
        >
          Download Certificate
        </Button>
      )}
      <Loading
        entities={[
          eventsStatus,
          quizStatus,
          sessionResultsStatus,
          completionEventsStatus,
          ceRecordsStatus,
        ]}
      />
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  ) : (
    <></>
  )
}
